import {
  cancelLogIn,
  logIn,
  logOut,
  selectAuth,
  selectIsLoggingIn
} from '../../ducks/auth';
import { fetchAllCountries, fetchEnums } from '../../ducks/entities';
import { useDispatch, useSelector } from 'react-redux';
import { useEffect, useMemo } from 'react';
import AppRoutes from './Routes';
import { Enum } from '../../models/enums';
import LoginModal from './LoginModal';
import { SORTBY_OPTIONS } from '../../utils/constants';
import accessibilitiesMessages from '../../components/AboutYou/messages';
import { createOptions } from '../../utils/generic';
import { setDevice } from '../../ducks/settings';
import siteMessages from './siteMessages';
import { sortBy } from 'lodash';
import { useIntl } from 'react-intl';
import { useSetState } from 'react-use';

export let createLocalizedOptions: any;

function App() {
  const intl = useIntl();
  const dispatch = useDispatch();
  const auth = useSelector(selectAuth);
  const isLoggingIn = useSelector(selectIsLoggingIn);
  const [loginModal, setLoginModal] = useSetState({
    open: false
  });

  createLocalizedOptions = (
    enums: Enum[],
    messages: Object,
    sortByOption = SORTBY_OPTIONS.Id,
    sortAsc = true
  ) => {
    const options = createOptions(
      enums.map((e: Enum) =>
        e.name.toLowerCase() == 'none'
          ? {
              id: e.id,
              name: intl.formatMessage({ ...siteMessages.NONE }),
              groupId: e.groupId ?? 0,
              index: e.index ?? 0
            }
          : e.name == 'gamingGenres.N/A'
          ? {
              id: e.id,
              name: intl.formatMessage({ ...siteMessages.NAMobile }),
              groupId: e.groupId ?? 0,
              index: e.index ?? 0
            }
          : e.name == 'socialHabits.N/A'
          ? {
              id: e.id,
              name: intl.formatMessage({ ...siteMessages.NASocialHabits }),
              groupId: e.groupId ?? 0,
              index: e.index ?? 0
            }
          : e.name == 'accessibilities.Na'
          ? {
              id: e.id,
              name: intl.formatMessage({ ...siteMessages.NAAccessibilities }),
              groupId: e.groupId ?? 0,
              index: e.index ?? 0
            }
          : {
              id: e.id,
              // eslint-disable-next-line
              // @ts-ignore
              name: intl.formatMessage({ ...messages[e.name] }),
              groupId: e.groupId ?? 0,
              index: e.index ?? 0
            }
      ),
      sortByOption,
      sortAsc
    );
    let sortedOptions = sortBy(options, (option) =>
      [
        intl.formatMessage({ ...siteMessages.NONE }),
        intl.formatMessage({ ...siteMessages.NAMobile }),
        intl.formatMessage({ ...siteMessages.NASocialHabits })
      ].includes(option.label)
        ? 0
        : 1
    );
    sortedOptions = sortBy(sortedOptions, (option) =>
      [
        intl.formatMessage({
          ...accessibilitiesMessages['accessibilities.Other']
        })
      ].includes(option.label)
        ? 1
        : 0
    );
    return sortedOptions;
  };

  useMemo(() => {
    if (!isLoggingIn && !auth.userId) {
      // Rejects when no Ubisoft SSO session exists, the expected case for public candidates.
      (dispatch(logIn()) as unknown as Promise<unknown>).catch(() => {});
      setLoginModal({ open: false });
    }
  }, [auth.userId]);

  useEffect(() => {
    if (auth.userId) {
      dispatch(fetchEnums());
      dispatch(fetchAllCountries());
    }
  }, [auth.userId]);

  useEffect(() => {
    const regex =
      /Mobi|Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i;
    const isMobile = regex.test(navigator.userAgent);
    dispatch(setDevice(isMobile));
  }, []);

  // Close the modal and remove any URL search query
  // This will prevent from opening the modal again due to the `useEffect`
  const onCloseLoginModal = () => {
    setLoginModal({ open: false });
    dispatch(cancelLogIn());
  };

  const onClickLogin = () => {
    setLoginModal({ open: true });
    (dispatch(logIn()) as unknown as Promise<unknown>).catch(() => {});
  };

  const onClickLogout = () => {
    dispatch(logOut());
  };

  return (
    <>
      <LoginModal open={loginModal.open} onClose={onCloseLoginModal} />
      <AppRoutes onClickLogin={onClickLogin} onClickLogout={onClickLogout} />
    </>
  );
}

export default App;
